import { computed, watchEffect, type ComputedRef } from 'vue';
import type { NxTableGroup } from '../internal';
import {
  partitionRowGroups,
  renderRowGroups,
  sameGroup,
  sameRowGroupLayout,
  type RowGroupHeader,
  type RowGroupLayout,
  type RowGroupOptions,
} from './rowGroups';
import { deriveTree } from './rowTree';

const NO_HEADERS: readonly RowGroupHeader<never>[] = Object.freeze([]);
const NO_KEYS: readonly string[] = Object.freeze([]);

/**
 * Row grouping of NxTable (`groupBy`). Everything row-index based in the table
 * — navigation, selection, the virtual list — runs on `viewRows`, the rows in
 * display order, where group headers are not rows at all: keyboard navigation,
 * selection, counts and the CSV export never see them. Indices handed to the
 * consumer are translated back with `toDataIndex`.
 *
 * Inactive (no `groupBy`, or `suspended`), `viewRows` IS `data` — the same array
 * — and both index maps are the identity, so an ungrouped table behaves exactly
 * as before.
 */
export function useRowGroups<T extends Record<string, unknown>>(params: {
  data: ComputedRef<T[]>;
  rowIdKey: (row: T) => string;
  groupBy: ComputedRef<((row: T) => string | null | undefined) | undefined>;
  groups: ComputedRef<readonly NxTableGroup[] | undefined>;
  ungroupedLabel: ComputedRef<string>;
  showEmptyGroups: ComputedRef<boolean>;
  /** Tree rows: a subtree stays with its top-level row. */
  rowParentId: ComputedRef<((row: T) => string | null) | undefined>;
  isPlaceholderRow?: (row: T) => boolean;
  /** Row reordering (`onRowMoved`) needs the rows in data order: no grouping then. */
  suspended: ComputedRef<boolean>;
}) {
  const warned = new Set<string>();
  const warn = import.meta.env.DEV
    ? (message: string) => {
        if (warned.has(message)) return;
        warned.add(message);
        console.warn(message);
      }
    : undefined;

  const active = computed(() => !!params.groupBy.value && !params.suspended.value);

  if (warn) {
    watchEffect(() => {
      if (params.groupBy.value && params.suspended.value) {
        warn(
          'NxTable groupBy: grouping is off while onRowMoved is set — reordering rows needs them in the order of `data`.'
        );
      }
    });
  }

  // Consumers write `:groups="[…]"` inline. Compare by value so a parent
  // re-render with the same groups does not re-partition every row.
  const groups = computed<readonly NxTableGroup[] | undefined>((previous) => {
    const next = params.groups.value;
    if (
      previous &&
      next &&
      previous.length === next.length &&
      previous.every((group, i) => sameGroup(group, next[i]))
    ) {
      return previous;
    }
    return next;
  });

  function options(rows: readonly T[]): RowGroupOptions<T> {
    const parentIdOf = params.rowParentId.value;
    let units: RowGroupOptions<T>['units'];
    if (parentIdOf) {
      const tree = deriveTree(rows, params.rowIdKey, parentIdOf, warn);
      units = (tree.childrenOf.get(null) ?? []).map((start) => ({
        start,
        size: tree.sizeOf[start],
      }));
    }
    return {
      groupBy: params.groupBy.value!,
      groups: groups.value,
      ungroupedLabel: params.ungroupedLabel.value,
      showEmptyGroups: params.showEmptyGroups.value,
      isPlaceholderRow: params.isPlaceholderRow,
      units,
      warn,
    };
  }

  const layout = computed<RowGroupLayout | null>((previous) => {
    if (!active.value) return null;
    const rows = params.data.value;
    const next = partitionRowGroups(rows, options(rows));
    if (warn && params.isPlaceholderRow && rows.some((row) => params.isPlaceholderRow!(row))) {
      warn(
        'NxTable groupBy: placeholder rows (isPlaceholderRow) have no group yet and are listed after all groups; ' +
          'windowed loading does not mix with grouping (visible-range-change counts display positions).'
      );
    }
    return previous && sameRowGroupLayout(previous, next) ? previous : next;
  });

  /** Display position → index in `data` (and back). Identity while inactive. */
  const inverse = computed(() => {
    const order = layout.value?.order;
    if (!order) return null;
    const map = new Int32Array(order.length);
    order.forEach((dataIdx, viewIdx) => (map[dataIdx] = viewIdx));
    return map;
  });

  const viewRows = computed<T[]>((previous) => {
    const rows = params.data.value;
    const order = layout.value?.order;
    if (!order) return rows;
    const next = order.map((dataIdx) => rows[dataIdx]);
    if (previous && previous !== rows && previous.length === next.length) {
      let same = true;
      for (let i = 0; i < next.length && same; i++) same = previous[i] === next[i];
      if (same) return previous;
    }
    return next;
  });

  const render = computed(() =>
    layout.value ? renderRowGroups<T>(params.data.value, layout.value) : null
  );

  function toDataIndex(viewIdx: number): number {
    const order = layout.value?.order;
    return order && viewIdx >= 0 && viewIdx < order.length ? order[viewIdx] : viewIdx;
  }

  function toViewIndex(dataIdx: number): number {
    const map = inverse.value;
    return map && dataIdx >= 0 && dataIdx < map.length ? map[dataIdx] : dataIdx;
  }

  /** Headers rendered directly above the row at display position `viewIdx`. */
  function headersAt(viewIdx: number): readonly RowGroupHeader<T>[] {
    return render.value?.byAnchor.get(viewIdx) ?? (NO_HEADERS as readonly RowGroupHeader<T>[]);
  }

  /** Measurement keys of those headers, for the virtual list. */
  function leadingKeysOf(viewIdx: number): readonly string[] {
    const headers = render.value?.byAnchor.get(viewIdx);
    return headers ? headers.map((header) => header.key) : NO_KEYS;
  }

  const trailingHeaders = computed(
    () => render.value?.trailing ?? (NO_HEADERS as readonly RowGroupHeader<T>[])
  );

  const headerCount = computed(() => render.value?.headers.length ?? 0);

  /** Number of header rows at or above the row at `viewIdx` (its `aria-rowindex` offset). */
  function headersThrough(viewIdx: number): number {
    const headers = render.value?.headers;
    if (!headers) return 0;
    let count = 0;
    for (const header of headers) {
      if (header.anchor > viewIdx) break;
      count++;
    }
    return count;
  }

  /** Row index inside its group — zebra stripes restart under every header. */
  function stripeIndex(viewIdx: number): number {
    const starts = render.value?.sectionStart;
    if (!starts || viewIdx < 0 || viewIdx >= starts.length) return viewIdx;
    return viewIdx - starts[viewIdx];
  }

  /** `rows` in display order (the CSV export of a grouped table). */
  function orderRows(rows: T[]): T[] {
    if (!active.value) return rows;
    return partitionRowGroups(rows, options(rows)).order.map((dataIdx) => rows[dataIdx]);
  }

  return {
    active,
    viewRows,
    hasHeaders: computed(() => headerCount.value > 0),
    headerCount,
    trailingHeaders,
    toDataIndex,
    toViewIndex,
    headersAt,
    leadingKeysOf,
    headersThrough,
    stripeIndex,
    orderRows,
  };
}
